import { useState } from 'react';
import { motion } from 'framer-motion';
import { PLANS } from './data';

// PRICING — three plans + live calculator. NOT a generic 3-card pricing.
export default function Pricing() {
  const [invocations, setInvocations] = useState(8);   // millions / mo
  const [seats, setSeats] = useState(12);
  const [retention, setRetention] = useState('14d');
  const subtotal = (() => {
    if (invocations <= 1 && seats <= 1) return 0;
    if (invocations <= 25 && seats <= 25 && retention !== '90d') return 290;
    return 290 + (invocations > 25 ? Math.ceil((invocations - 25)) * 18 : 0) + (seats > 25 ? (seats - 25) * 24 : 0) + (retention === '90d' ? 240 : 0);
  })();

  return (
    <div className="bg-[#0A0E14] text-[#E6EDF3] font-mono min-h-screen">
      <section className="pt-32 pb-8 max-w-[1500px] mx-auto px-5 lg:px-8 border-b border-[#1A2230]">
        <p className="text-[10px] tracking-[0.32em] text-[#06D6A0] uppercase mb-3">// console / pricing · plan --tier</p>
        <h1 className="font-sans text-5xl lg:text-7xl tracking-[-0.025em] lowercase leading-[0.98] font-medium">three tiers.<br /><span className="text-[#5F7184]">no surprise invoices.</span></h1>
      </section>

      {/* THREE PLAN COLUMNS */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-8 py-12 grid md:grid-cols-3 gap-5">
        {PLANS.map((p, i) => (
          <motion.article key={p.tier} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }} className={`border bg-[#0E141C] rounded-sm flex flex-col ${p.featured ? 'border-[#06D6A0] shadow-[0_0_60px_rgba(6,214,160,0.18)]' : 'border-[#1A2230]'}`}>
            <header className="px-6 py-5 border-b border-[#1A2230]">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#5F7184]">// tier</p>
              <p className="font-sans text-2xl text-[#E6EDF3] lowercase mt-1">{p.tier}</p>
              <p className="text-[10px] text-[#5F7184] mt-1">{p.for}</p>
              <div className="mt-5 flex items-baseline gap-1.5">
                {p.price === 'custom' ? (<p className="font-sans text-4xl text-[#E6EDF3]">custom</p>) : (<><span className="text-xl text-[#5F7184]">$</span><p className="font-sans text-5xl text-[#E6EDF3]">{p.price}</p></>)}
                <span className="text-xs text-[#5F7184]">{p.unit}</span>
              </div>
            </header>
            <ul className="px-6 py-5 space-y-2.5 text-xs flex-1">
              {p.items.map(it => (
                <li key={it} className="flex items-start gap-2 text-[#E6EDF3]/85"><span className="text-[#06D6A0] shrink-0">✓</span><span>{it}</span></li>
              ))}
            </ul>
            <button data-testid={`mc-plan-${p.tier}`} className={`mx-6 mb-6 py-3 text-sm transition-colors ${p.featured ? 'bg-[#06D6A0] text-[#0A0E14] hover:bg-[#5BFFC9]' : 'border border-[#5F7184]/40 text-[#E6EDF3] hover:border-[#06D6A0] hover:text-[#06D6A0]'}`}>$ start --tier {p.tier}</button>
          </motion.article>
        ))}
      </section>

      {/* LIVE CALCULATOR */}
      <section className="max-w-[1500px] mx-auto px-5 lg:px-8 py-10">
        <div className="border border-[#1A2230] bg-[#0E141C] rounded-sm">
          <div className="px-6 py-4 border-b border-[#1A2230] flex items-baseline justify-between">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#5F7184]">// live calculator · plan --estimate</p>
            <p className="text-[10px] text-[#06D6A0]">● recompiles on change</p>
          </div>
          <div className="p-6 lg:p-9 grid lg:grid-cols-[1fr_280px] gap-10">
            <div className="space-y-6">
              <Slider label="Edge invocations" unit="M / month" value={invocations} setValue={setInvocations} min={1} max={120} testId="mc-calc-inv" />
              <Slider label="Seats on the team" unit="people" value={seats} setValue={setSeats} min={1} max={120} testId="mc-calc-seats" />
              <div>
                <label className="text-[10px] tracking-[0.32em] uppercase text-[#5F7184] block mb-2">Log retention</label>
                <div className="flex gap-2">
                  {['24h','14d','90d'].map(opt => (
                    <button key={opt} data-testid={`mc-calc-ret-${opt}`} onClick={() => setRetention(opt)} className={`px-4 py-2 text-xs transition-colors ${retention === opt ? 'bg-[#06D6A0] text-[#0A0E14]' : 'border border-[#5F7184]/40 text-[#E6EDF3] hover:border-[#06D6A0]'}`}>{opt}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="border border-[#06D6A0]/30 bg-[#06D6A0]/5 p-6 rounded-sm">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#5F7184]">// estimated bill</p>
              <p className="font-sans text-5xl text-[#06D6A0] mt-2">${subtotal}<span className="text-base text-[#5F7184]"> /mo</span></p>
              <p className="text-[10px] text-[#5F7184] mt-2 leading-[1.7]">Annual contract = 2 months free. Includes all four modules. No per-seat overage on the developer plan.</p>
              <button data-testid="mc-calc-cta" className="w-full mt-5 bg-[#06D6A0] text-[#0A0E14] py-3 text-sm hover:bg-[#5BFFC9] transition-colors">$ checkout --bill ${subtotal}</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Slider({ label, unit, value, setValue, min, max, testId }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <label className="text-[10px] tracking-[0.32em] uppercase text-[#5F7184]">{label}</label>
        <span className="font-sans text-xl text-[#E6EDF3]">{value}<span className="text-xs text-[#5F7184] ml-1">{unit}</span></span>
      </div>
      <input type="range" data-testid={testId} min={min} max={max} value={value} onChange={(e) => setValue(Number(e.target.value))} className="w-full accent-[#06D6A0]" />
    </div>
  );
}
